import createStore from "../configureStore";
import rootReducer from "../reducers";
import {
  ANIMATION_GET_GOAL_ACHIEVEMENTS,
  ANIMATION_GET_GOAL_PLAY,
  ANIMATION_GET_GOAL_REWARDS,
  LOCAL_STORAGE_KEY__LOCALE,
  PLAYER_SIMPLE_URI_DETAIL_PARAMETER,
  PLAYER_SIMPLE_URI_PREFIX,
  PLAYER_SIMPLE_URI_SUFFIX,
  ROUTES_BY_WIDGET_ACTION,
  WIDGET_ACTION_DETAIL,
  TYPE_STRING
} from "./Constants";
import { CONTENT_TYPE_CONTAINER } from "../../../detail/src/Pages/DetailPage/PlaylistConstants";
import defaultBanner from "@app/Resources/Images/default_banner.png";
import {
  URL_DETAIL,
  URL_HOME,
  URL_VIDEODETAIL,
  URL_SECTION,
  URL_SCREEN,
  URL_NAME_EXCEPTIONS,
  URL_PATH_NAMES,
  URL_DISCOVER,
  URL_BUSCAR,
} from "../Components/Routes/Location";
import jwtDecode from "jwt-decode";
import { MULTITENANT_CONSTANTS, TENANT } from "../Resources/Multitenant/tenantConstants";
import Cookies from "js-cookie";
import {getPreview} from "../services/services";
import { isCordovaAvailable } from "./secureStorageUtil";
import i18n from "../i18n";
import {URL_BASE_IMAGE, URL_BASE_V2} from "../services/servicesConstants";
import {
  EDITORIAL_TAGS_PRIORITY,
  AGE_RESTRICTION_7_PLUS,
  AGE_RESTRICTION_12_PLUS,
  AGE_RESTRICTION_16_PLUS,
  AGE_RESTRICTION_18_PLUS,
  SECTION_TYPE_SCREEN,
} from "./Constants";
import { PLAYERDETAIL_VALUE } from "../analyticsConstants/analyticsConstants";
import keycloakSingleton from "./keycloak";

export const store = createStore(rootReducer);

export const setAchievementsAndRewardsInLocalStorage = (o) => {
  const achievements = o?.actionResult?.achievements;
  const rewards = o?.actionResult?.rewards;
  if (achievements?.length > 0 || rewards?.length > 0) {
    localStorage.setItem(ANIMATION_GET_GOAL_PLAY, JSON.stringify(1));
    localStorage.setItem(ANIMATION_GET_GOAL_ACHIEVEMENTS, JSON.stringify(!!achievements[0] ? achievements[0] : {}));
    localStorage.setItem(ANIMATION_GET_GOAL_REWARDS, JSON.stringify(!!rewards[0] ? rewards[0] : {}));
  }
};

export const getPlayerSimpleURL = (id, isDetail) => {
  if (isDetail) {
    return `${URL_BASE_V2}${PLAYER_SIMPLE_URI_PREFIX}/${id}${PLAYER_SIMPLE_URI_SUFFIX}${PLAYER_SIMPLE_URI_DETAIL_PARAMETER}`;
  } else {
    return `${URL_BASE_V2}${PLAYER_SIMPLE_URI_PREFIX}/${id}${PLAYER_SIMPLE_URI_SUFFIX}`; 
  }
}

export const getSectionUrl = (section) => {
  if (!section?.name) {
    return URL_HOME;
  }

  const sectionNameUpper = section.name.toUpperCase();
  const sectionTypeUpper = section?.type?.toUpperCase() || "";

  if (sectionTypeUpper === SECTION_TYPE_SCREEN.toUpperCase()) {
    if (URL_NAME_EXCEPTIONS.includes(sectionNameUpper)) {
      return `/${section.name.toLowerCase()}`;
    }

    const slugName = generateSlug(section.name);
    return section.screenId
      ? `${URL_SCREEN}/${slugName}/${section.screenId}`
      : `${URL_SCREEN}/${slugName}`;
  }

  if (sectionTypeUpper && URL_PATH_NAMES.includes(sectionTypeUpper)) {
    return `/${section.type.toLowerCase()}`;
  }

  return `${URL_SECTION}/${generateSlug(section.name)}`;
};

export const isContentTypeContainer = (typeName) => {
  return typeName === CONTENT_TYPE_CONTAINER;
};

export const actionTypeContainer = (widgetAction) => {
  return !!widgetAction
    ? ROUTES_BY_WIDGET_ACTION[widgetAction.toUpperCase()] || ROUTES_BY_WIDGET_ACTION[WIDGET_ACTION_DETAIL]
    : "/";
};

export const getContrastColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#000000';

  hex = hex.replace('#', '').trim();

  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  if (hex.length !== 6) return '#000000';

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#000000';

  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// Luminancia relativa WCAG de un color #rgb/#rrggbb; null si no se puede leer.
const getRelativeLuminance = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(h.substring(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** Ratio de contraste WCAG (1-21) entre dos colores; null si alguno no es un hex válido. */
export const getContrastRatio = (hexA, hexB) => {
  const a = getRelativeLuminance(hexA);
  const b = getRelativeLuminance(hexB);
  if (a === null || b === null) return null;
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

// WCAG 1.4.11 pide 3:1 para los límites de un componente; se baja a 2.5 para respetar colores de
// marca que ya se distinguen bien aunque se queden algo por debajo (bolerus: verde sobre gris, 2.8:1).
// El texto sí mantiene el 4.5:1 de WCAG.
const MIN_COMPONENT_CONTRAST = 2.5;
const MIN_TEXT_CONTRAST = 4.5;

/**
 * Colores del botón principal para que se distinga del fondo de la página: se usa el primer
 * candidato (botón por defecto, color de acento, color de texto de la página) con 3:1 sobre la
 * página. El texto configurado solo se recalcula cuando cambia el fondo y no llega a 4.5:1.
 */
export const getContrastingButtonColors = ({ pageColor, buttonColor, buttonTextColor, accentColor, pageTextColor }) => {
  const contrasts = (color) => (getContrastRatio(color, pageColor) ?? 0) >= MIN_COMPONENT_CONTRAST;
  // Sin un color de página legible no hay nada que comprobar.
  if (getRelativeLuminance(pageColor) === null || contrasts(buttonColor)) {
    return { background: buttonColor, text: buttonTextColor };
  }
  const background = [accentColor, pageTextColor].find(contrasts) ?? getContrastColor(pageColor);
  const text =
    (getContrastRatio(buttonTextColor, background) ?? 0) >= MIN_TEXT_CONTRAST
      ? buttonTextColor
      : getContrastRatio('#000000', background) > getContrastRatio('#FFFFFF', background)
        ? '#000000'
        : '#FFFFFF';
  return { background, text };
};

// OTT-1966 Fase 4: /discover redirige a /buscar, así que una sección cuya url es /discover
// debe marcarse activa también en /buscar. Eliminar cuando el menú apunte directamente a /buscar.
const SECTION_URL_ALIASES = {
  [URL_DISCOVER]: [URL_BUSCAR],
};

const matchesSectionUrl = (url, pathname) => pathname === url || pathname.startsWith(`${url}/`);

export const isSectionActive = (section, pathname) => {
  const url = section?.url || (section?.section ? getSectionUrl(section.section) : null);
  if (!url) return false;
  return [url, ...(SECTION_URL_ALIASES[url] || [])].some((candidate) => matchesSectionUrl(candidate, pathname));
}

export const isAnyDescendantActive = (section, pathname) => {
  if (!section.children?.length) return false;
  return section.children.some(
    child => isSectionActive(child, pathname) || isAnyDescendantActive(child, pathname)
  );
};

export const checkMenuActive = (match, location, a, sectionSelected, sections) => {
  if (isSectionActive(a, location.pathname) || isAnyDescendantActive(a, location.pathname)) {
    return true;
  }

  const isFirstHomeSection = sections?.indexOf(a) === 0;
  return location.pathname === URL_HOME && !!isFirstHomeSection;
};

export const handleShowPassword = (numberPassword, showPassword) => {
  let arrayInputs = [];
  switch (numberPassword) {
    case 0:
      arrayInputs = [!showPassword[0], showPassword[1], showPassword[2]];
      break;
    case 1:
      arrayInputs = [showPassword[0], !showPassword[1], showPassword[2]];
      break;
    case 2:
      arrayInputs = [showPassword[0], showPassword[1], !showPassword[2]];
      break;
    default:
      return arrayInputs;
  }
  return arrayInputs;
};

export const renderImage = (content) => {
  return content.banner?.url ? content.banner.url : defaultBanner;
};

export const renderImageBannerData = (contentBannerData) => {
  let urlImage = null;

  let bannerUrl = typeof contentBannerData?.banner === TYPE_STRING
    ? contentBannerData.banner
    : contentBannerData?.banner?.data?.url
    ? `${URL_BASE_IMAGE}${contentBannerData.banner.data.url}`
    : null;

  let backgroundUrl = typeof contentBannerData?.background === TYPE_STRING
    ? contentBannerData.background
    : contentBannerData?.background?.id?.data?.asset_url
    ? `${URL_BASE_IMAGE}${contentBannerData.background.id.data.asset_url}`
    : null;
  if (bannerUrl) {
    urlImage = bannerUrl;
  } else if (backgroundUrl) {
    urlImage = backgroundUrl;
  } else {
    urlImage = defaultBanner;
  }
  return urlImage;
};

export const renderImageUrl = (url) => {
  return `${url}`;
};

export const generateSlug = (text) => {
  const removeAccents = (str) => {
    const accents = [
      /[\300-\306]/g,
      /[\340-\346]/g, // A, a
      /[\310-\313]/g,
      /[\350-\353]/g, // E, e
      /[\314-\317]/g,
      /[\354-\357]/g, // I, i
      /[\322-\330]/g,
      /[\362-\370]/g, // O, o
      /[\331-\334]/g,
      /[\371-\374]/g, // U, u
      /[\321]/g,
      /[\361]/g, // N, n
      /[\307]/g,
      /[\347]/g, // C, c
    ];

    const nonAccents = ["A", "a", "E", "e", "I", "i", "O", "o", "U", "u", "N", "n", "C", "c"];

    for (let i = 0; i < accents.length; i++) {
      str = str.replace(accents[i], nonAccents[i]);
    }

    return str;
  };

  return removeAccents(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, "") // Remover caracteres especiales
    .replace(/\s+/g, "-") // Reemplazar espacios en blanco por guiones
    .replace(/--+/g, "-") // Reemplazar múltiples guiones por uno solo
    .trim(); // Eliminar espacios en blanco al principio y al final
};

export const decodeToken = (token) => {
  const userData = jwtDecode(token);

  return userData.locale;
};

export const setHtmlLang = (lang) => {
  document.documentElement.lang = lang;
};

export const getTranslatedName = (node) => {
  const translation = node?.translations?.find((t) => t.language === getLang());
  return translation?.name || node?.name; // Si hay traducción, usa esa, sino usa el nombre por defecto
};

export const getTranslatedCategory = (category) => {
  const translation = category?.current?.translations?.find((t) => t.language === getLang());
  return translation?.name || category?.current?.name;
};
export const getTranslatedSubsectionName = (subsection) => {
  const translation = subsection?.translations?.find((t) => t.language === getLang());

  // Si encuentra una traducción, devuelve el nombre traducido, si no, el nombre por defecto
  return translation?.name || subsection?.name;
};
export const getLang = () => {
  if (isCordovaAvailable()) {
    return i18n.language;
  }
  let language = localStorage.getItem(LOCAL_STORAGE_KEY__LOCALE);
  if (!language) {
    language = document.documentElement.lang;
    if (!language) {
      language = MULTITENANT_CONSTANTS[TENANT].language;
    }
  }
  return language;
};

const isTokenExpired = (token) => {
  if (!token) {
    return true;
  }
  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Tiempo actual en segundos
    return decodedToken && decodedToken.exp  < currentTime && decodedToken.sub;
  } catch (error) {
    return true;
  }
};

const getTokenFromCookie = () => {
  const validToken = !isTokenExpired(Cookies.get("TIIVII_accessToken"));
  return validToken ? Cookies.get("TIIVII_accessToken") : null;
};

const isTokenValidForClient = (cookieToken, keycloakToken) => {
  if (!cookieToken || !keycloakToken) return false;

  const decodedCookieToken = jwtDecode(cookieToken);
  const decodedKeycloakToken = jwtDecode(keycloakToken);

  return decodedCookieToken.sub === decodedKeycloakToken.sub;
};

export const getToken = (keycloak) => {
  let cookieToken = getTokenFromCookie();
  // In Cordova, useKeycloak() may not detect manually set tokens
  // Fall back to the singleton instance if the provided keycloak doesn't have a token
  const keycloakToken = keycloak?.token || keycloakSingleton?.token;
  const isTheSameUser = isTokenValidForClient(cookieToken, keycloakToken);
  if ((!cookieToken && !!keycloakToken) || (!!keycloakToken && !isTheSameUser)) {
      saveTokenToCookie(keycloakToken);
      cookieToken = keycloakToken
    } else cookieToken = keycloakToken ?? "";
  return cookieToken;
}

const saveTokenToCookie = (token) => {
  Cookies.remove('TIIVII_accessToken');
  Cookies.set('TIIVII_accessToken', token, { expires: 60 }); // La cookie expirará en 60 días
};

export const removeTokenFromCookie = () => {
  Cookies.remove('TIIVII_accessToken');
};

export const getSectionName = (section) => {
  let name = section?.translations?.find((t) => t.language === getLang())?.name;
  if (!name) {
    name = section?.name;
  }
  return name;
};

export const getWidgetTitle = (widget) => {
  let title = widget?.translations?.find((t) => t.language === getLang())?.title;
  if (!title) {
    title = widget?.title;
  }
  return title;
};
export const getWidgetDescription = (widget) => {
  let description = widget?.translations?.find((t) => t.language === getLang())?.description;
  if (!description) {
    description = widget?.description;
  }
  return description;
};
export const getWidgetSubtitle = (widget) => {
  let subtitle = widget?.translations?.find((t) => t.language === getLang())?.subtitle;
  if (!subtitle) {
    subtitle = widget?.subtitle;
  }
  return subtitle;
};

export const getContentCodeByLanguage = (content) => {
  let title = content?.translations?.find((t) => t.language === getLang())?.code;
  if (!title) {
    title = content?.code;
  }
  return title;
};

const callbackPreview = (data) => {
  const isMobile = false;
  let urlPreview = "";
  const getUrl = (source) => {
    return source?.webm || source?.mp4 || null;
  };

  if (isMobile) {
    urlPreview = getUrl(data?.mobile) || getUrl(data?.web);
  } else {
    urlPreview = getUrl(data?.web) || getUrl(data?.mobile);
  }
  return urlPreview;
};

export const getPreviewUrl = async (contentId) => {
  try {
    const data = await getPreview(contentId);
    const urlPreview = callbackPreview(data);
    return urlPreview;
  } catch (error) {
    console.error("Error fetching preview URL:", error);
  }
}

export const getEditorialTag = (editorialTags, locale) => {
  let selectedTag = null;
  for (const tag of editorialTags) {
    let tagData = tag;

    if (tag.editorial_tags_id) {
      tagData = tag.editorial_tags_id;
    }

    const tagTitle = tagData.title?.toLowerCase() || '';
    if (EDITORIAL_TAGS_PRIORITY.some(priority => tagTitle.includes(priority))) {
      selectedTag = tagData;
      break;
    }
  }

  if (!selectedTag) {
    let firstTag = editorialTags[0];
    if (firstTag.editorial_tags_id) {
      selectedTag = firstTag.editorial_tags_id;
    } else {
      selectedTag = firstTag;
    }
  }

  const translation = selectedTag.translations?.find(t => t.language === locale);
  const title = translation?.title || selectedTag.title;
  return {
    title,
    backgroundColor: selectedTag?.backgroundcolor,
    textColor: selectedTag?.textcolor
  };
};

// Menu Utilities - Funciones para la funcionalidad del menú lateral condicional
// Especialmente para el tenant ORION que oculta el menú en HOME

/**
 * Determina si la ruta actual es la página HOME
 * @param {string} pathname - La ruta actual (history.location.pathname)
 * @returns {boolean} - true si es la página HOME
 */
export const isHomePage = (pathname) => {
  const { MENU_HOME_PATHS } = require('./Constants');
  return MENU_HOME_PATHS.includes(pathname);
};

/**
 * Determina si la vista actual es desktop (no móvil)
 * @param {boolean} renderMobileVersion - Flag que indica si se está renderizando versión móvil
 * @returns {boolean} - true si es vista desktop
 */
export const isDesktopView = (renderMobileVersion) => {
  const { MENU_DESKTOP_BREAKPOINT } = require('./Constants');
  return !renderMobileVersion && window.innerWidth > MENU_DESKTOP_BREAKPOINT;
};

/**
 * Obtiene la configuración del tenant para ocultar menú en HOME
 * @returns {boolean} - true si el tenant tiene configurado ocultar el menú en HOME
 */
export const shouldHideMenuOnHome = () => {
  return MULTITENANT_CONSTANTS[TENANT]?.hideMenuOnHome || false;
};

/**
 * Determina si debe ocultarse/no renderizarse el menú lateral
 * Combina todas las condiciones: HOME + Desktop + Configuración del tenant
 * @param {string} pathname - La ruta actual
 * @param {boolean} renderMobileVersion - Flag de versión móvil
 * @returns {boolean} - true si debe ocultarse el menú lateral
 */
export const shouldHideLateralMenu = (pathname, renderMobileVersion) => {
  return isHomePage(pathname) && 
         isDesktopView(renderMobileVersion) && 
         shouldHideMenuOnHome();
};

/**
 * Obtiene la clase CSS apropiada para el contenedor del menú
 * @param {string} pathname - La ruta actual
 * @param {boolean} renderMobileVersion - Flag de versión móvil
 * @returns {string} - Clase CSS apropiada
 */
export const getMenuClassName = (pathname, renderMobileVersion) => {
  const { MENU_ORION_CSS_CLASS, MENU_DEFAULT_CSS_CLASS } = require('./Constants');
  return shouldHideLateralMenu(pathname, renderMobileVersion) 
    ? MENU_ORION_CSS_CLASS 
    : MENU_DEFAULT_CSS_CLASS;
};

/**
 * Gestiona la clase CSS en el body para el fallback de navegadores sin soporte :has()
 * @param {string} pathname - La ruta actual
 * @param {boolean} renderMobileVersion - Flag de versión móvil
 */
export const manageBodyClass = (pathname, renderMobileVersion) => {
  const { MENU_BODY_ACTIVE_CLASS } = require('./Constants');
  const shouldHide = shouldHideLateralMenu(pathname, renderMobileVersion);
  
  if (shouldHide) {
    document.body.classList.add(MENU_BODY_ACTIVE_CLASS);
  } else {
    document.body.classList.remove(MENU_BODY_ACTIVE_CLASS);
  }
};

/**
 * Limpia la clase CSS del body (para cleanup en useEffect)
 */
export const cleanupBodyClass = () => {
  const { MENU_BODY_ACTIVE_CLASS } = require('./Constants');
  document.body.classList.remove(MENU_BODY_ACTIVE_CLASS);
};

export const getAgeRestrictionLabel = (code) => {
  switch (code) {
    case AGE_RESTRICTION_7_PLUS:
    case AGE_RESTRICTION_12_PLUS:
    case AGE_RESTRICTION_16_PLUS:
    case AGE_RESTRICTION_18_PLUS:
      return code;
    default:
      return null;
  }
};

export const getDefaultDetailBaseURL = () => {
  return MULTITENANT_CONSTANTS[TENANT]?.defaultDetailVersion === PLAYERDETAIL_VALUE ? URL_DETAIL : URL_VIDEODETAIL;
}

// Textos por idioma configurados en la interfaz (`interfaz.translations`: ppv_botton, ppv_modal_text...).
// Si el idioma no tiene entrada o el campo llega vacío, se usa `fallback` (normalmente un texto de
// messages_*.json) en lugar de pintar la clave o un botón vacío.
export const getInterfaceTranslation = (translations, language, field, fallback) =>
  translations?.find((translation) => translation.language === language)?.[field] || fallback;
